/* eslint-disable no-console */
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { inject, observer } from 'mobx-react';
import { useEffect, useState } from 'react';

import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';

import {
  ArrowUpTrayIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';

import useActionsCapabilitiesStore from '@/Features/Core/Components/ActionsCapabilities/store';
import useThemeStore from '@/Theme/store';

// Components
import ImagesList from './components/ImagesList';
import UploadPanel from './components/UploadPanel';

// Hooks
import useImageFiles from './hooks/useImageFiles';

/**
 * Modal component for managing image attachments
 * @param {Object} props - Component props
 * @returns {JSX.Element} Modal component
 */
const ModalImagesAttachments = inject('store')(
  observer(
    ({
      store,
      onInteraction,
      onClose,
      models,
      onBadInteraction,
      defaultTab,
      onlySelectedOne,
      open = true,
    }) => {
      const [value, setValue] = useState(defaultTab ?? '1');
      const { theme } = useThemeStore();

      const handleChange = (event, newValue) => {
        setValue(newValue);
        // File selection is now handled by the hook
      };

      const {
        generationsModelsActive,
        selectedModelParameters,
        setSelectedModelParameters,
      } = useActionsCapabilitiesStore();

      const noVision = () => {
        if (generationsModelsActive) return false;
        return models?.every((model) => model.type !== 'vision');
      };

      const {
        files,
        fileSelected,
        selectedIndex,
        searchTerm,
        hasMore,
        handleInputChange,
        handleNext,
        selectFile,
        updateVisibilityFile,
      } = useImageFiles(store);

      const [uploadedFiles, setUploadedFiles] = useState([]);

      const improveSelectingImage = (hasSelectedImage) => {
        setSelectedModelParameters({
          ...selectedModelParameters,
          attachment: hasSelectedImage ? 1 : undefined,
        });
      };

      // Monitor changes in fileSelected and uploadedFiles to update attachment parameter
      useEffect(() => {
        const hasSelectedImage =
          (fileSelected && fileSelected.length > 0) ||
          (uploadedFiles && uploadedFiles.length > 0);
        improveSelectingImage(hasSelectedImage);
      }, [fileSelected, uploadedFiles]);

      const handleFileClick = (index) => {
        selectFile(index, onlySelectedOne);
        // We'll check the updated state in the next render
      };

      const handleFileUploaded = (uploadedFiles) => {
        setUploadedFiles(uploadedFiles);
        // Set the uploaded files as selected for the upload panel
        setValue('2'); // Stay on upload tab
        // The useEffect will handle updating the attachment parameter
      };

      // For upload panel, we need to manage fileSelected separately
      const currentFileSelected = value === '1' ? fileSelected : uploadedFiles;

      return (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '21px',
              fontFamily: 'Figtree',
              backgroundColor: theme === 'dark' ? '#000000' : '#F7F7F8',
            },
          }}
        >
          <DialogTitle
            sx={{
              fontFamily: 'Figtree',
              fontSize: '24px',
              fontWeight: 700,
              color: theme === 'dark' ? 'white' : 'black',
              pb: 1,
            }}
          >
            Image attachments
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <XMarkIcon
                className={`w-3 h-3 ${
                  theme === 'dark' ? 'text-quicksilver' : 'text-cool-gray'
                }`}
              />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0, pb: 3, px: 3 }}>
            <Box sx={{ width: '100%' }}>
              <TabContext value={value}>
                {/* Tabs */}
                <TabList
                  variant="fullWidth"
                  onChange={handleChange}
                  orientation="horizontal"
                >
                  <Tab
                    value="1"
                    label="Your images"
                    sx={{
                      fontFamily: 'Figtree',
                      fontSize: '14px',
                      fontWeight: 600,
                      textTransform: 'none',
                    }}
                    icon={<PhotoIcon className="w-5 h-5" />}
                  />
                  <Tab
                    value="2"
                    label="Upload image"
                    sx={{
                      fontFamily: 'Figtree',
                      fontSize: '14px',
                      fontWeight: 600,
                      textTransform: 'none',
                    }}
                    icon={<ArrowUpTrayIcon className="w-5 h-5" />}
                  />
                </TabList>

                {/* Panel 1 - Your Images */}
                <TabPanel value="1" sx={{ padding: '12px 0 0 0' }}>
                  <ImagesList
                    theme={theme}
                    files={files}
                    fileSelected={fileSelected}
                    selectedIndex={selectedIndex}
                    searchTerm={searchTerm}
                    hasMore={hasMore}
                    noVision={noVision()}
                    onInputChange={handleInputChange}
                    onNext={handleNext}
                    onFileClick={handleFileClick}
                    onBadInteraction={onBadInteraction}
                    onDeleteFile={updateVisibilityFile}
                    onInteraction={onInteraction}
                  />
                </TabPanel>

                {/* Panel 2 - Upload Image */}
                <TabPanel value="2" sx={{ padding: '12px 0 0 0' }}>
                  <UploadPanel
                    theme={theme}
                    noVision={noVision()}
                    fileSelected={currentFileSelected}
                    onInteraction={onInteraction}
                    onBadInteraction={onBadInteraction}
                    onUploaded={handleFileUploaded}
                  />
                </TabPanel>
              </TabContext>
            </Box>
          </DialogContent>
        </Dialog>
      );
    }
  )
);

export default ModalImagesAttachments;
